import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { Button, Dialog, Portal, Text, useTheme } from 'react-native-paper';
import { useIsFocused } from '@react-navigation/native';
import { Ionicons } from '@expo/vector-icons';
import { t, useLanguage } from '../i18n';
import { CALENDAR_ANNOUNCEMENT_ID, dismissFeatureAnnouncement, isFeatureAnnouncementDismissed } from '../utils/featureAnnouncement';

type Props = { enabled: boolean; onOpenCalendar: () => void };

export default function NewFeatureAnnouncement({ enabled, onOpenCalendar }: Props) {
  useLanguage();
  const theme = useTheme();
  const { height } = useWindowDimensions();
  const isFocused = useIsFocused();
  const [unseen, setUnseen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Does not delay timetable rendering or wait for cloud data/syllabus loading.
    void isFeatureAnnouncementDismissed(CALENDAR_ANNOUNCEMENT_ID).then(dismissed => {
      if (!cancelled) setUnseen(!dismissed);
    });
    return () => { cancelled = true; };
  }, []);

  const close = () => {
    setUnseen(false);
    void dismissFeatureAnnouncement(CALENDAR_ANNOUNCEMENT_ID);
  };

  return (
    <Portal>
      <Dialog
        visible={unseen && enabled && isFocused}
        onDismiss={close}
        style={[styles.dialog, { backgroundColor: theme.colors.elevation.level3 }]}
      >
        <Dialog.Title>{t('新機能を追加しました')}</Dialog.Title>
        <Dialog.Content>
          <ScrollView style={{ maxHeight: Math.max(120, height - 240) }} showsVerticalScrollIndicator={false}>
            <View style={styles.featureHeader}>
              <View style={[styles.icon, { backgroundColor: theme.colors.primaryContainer }]}>
                <Ionicons name="calendar-outline" size={28} color={theme.colors.onPrimaryContainer} />
              </View>
              <Text variant="titleMedium" style={styles.featureTitle}>{t('毎週の授業をGoogleカレンダーへ')}</Text>
            </View>
            <Text style={styles.body}>{t('「設定」→「カレンダー連携」から、時間割の授業を毎週の予定として追加できます。授業詳細からも使えます。')}</Text>
            <View style={[styles.hint, { backgroundColor: theme.colors.surfaceVariant }]}>
              <Text style={styles.hintText}>{t('Google側で日時・繰り返しを確認し、最後に「保存」を押してください。')}</Text>
              <Text style={[styles.hintText, { color: theme.colors.onSurfaceVariant }]}>{t('休講・祝日は自動で除外されません。')}</Text>
            </View>
          </ScrollView>
        </Dialog.Content>
        <Dialog.Actions style={styles.actions}>
          <Button onPress={close}>{t('閉じる')}</Button>
          <Button mode="contained" onPress={() => { close(); onOpenCalendar(); }}>{t('カレンダー連携を開く')}</Button>
        </Dialog.Actions>
      </Dialog>
    </Portal>
  );
}

const styles = StyleSheet.create({
  dialog: { width: '92%', maxWidth: 420, alignSelf: 'center', marginHorizontal: 0, borderRadius: 20 },
  featureHeader: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 16 },
  icon: { width: 48, height: 48, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  featureTitle: { flex: 1, lineHeight: 24 },
  body: { lineHeight: 23, marginBottom: 16 },
  hint: { padding: 12, borderRadius: 12, gap: 8 },
  hintText: { fontSize: 13, lineHeight: 20 },
  actions: { flexWrap: 'wrap', gap: 8, paddingHorizontal: 16, paddingBottom: 16 },
});
